import { UserRetailAccount } from './types';
import { RefreshButtonWithMeta } from './RefreshButtonWithMeta';

export { RefreshIcon } from './RefreshIcon';

export type RetailAccountCardToolbarProps = {
  account: UserRetailAccount;
  refreshing: boolean;
  rebalancing: boolean;
  updating?: boolean;
  togglingSignalForge?: boolean;
  onDeactivate?: () => void;
  onRefresh: () => void;
  onToggleSignalForge?: () => void;
  onRebalance: () => void;
  lastUpdatedAt?: string | Date | null;
  showDeactivate?: boolean;
  showRefresh?: boolean;
  showSignalForge?: boolean;
  showRebalance?: boolean;
};

// Shared account-toolbar button recipe (was .account-toolbar-btn in accounts.css).
const TOOLBAR_BTN_BASE =
  'box-border inline-flex cursor-pointer items-center gap-2 self-stretch whitespace-nowrap rounded-[8px] border border-transparent px-3.5 py-2 text-[12px] font-semibold leading-[1.2] transition-[background,border-color,color] duration-150 disabled:cursor-not-allowed disabled:opacity-50';
const TOOLBAR_TONE_WARN =
  'bg-secondary/12 text-secondary border-secondary/35 enabled:hover:bg-secondary/20';
const TOOLBAR_TONE_ACTIVATE =
  'bg-positive/12 text-[color:var(--nq-up-bright)] border-positive/35 enabled:hover:bg-positive/20';
const TOOLBAR_TONE_REBALANCE =
  'bg-primary/12 text-[color:var(--nq-blue-bright)] border-primary/35 enabled:hover:bg-primary/20 enabled:hover:border-primary/50';
const TOOLBAR_TONE_SF_ON =
  'bg-secondary/14 text-secondary border-secondary/45 enabled:hover:bg-secondary/22 enabled:hover:border-secondary/60';
const TOOLBAR_TONE_SF_OFF =
  'bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.55)] border-[rgba(255,255,255,0.1)] enabled:hover:bg-[rgba(255,255,255,0.08)] enabled:hover:text-[rgba(255,255,255,0.85)] enabled:hover:border-[rgba(255,255,255,0.16)]';

const DeactivateIcon = () => (
  <svg
    className="block shrink-0"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M4.93 4.93l14.14 14.14" />
  </svg>
);

export const RebalanceIcon = ({ spinning = false }: { spinning?: boolean }) => (
  <svg
    className={`block shrink-0${spinning ? ' animate-spin' : ''}`}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const SignalForgeIcon = ({ on = true }: { on?: boolean }) => (
  <svg
    className={`block shrink-0${on ? '' : ' opacity-55'}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 18V14" />
    <path d="M8 18V10" />
    <path d="M12 18V12" />
    <path d="M16 18V6" />
    <path d="M16 2v2.5" />
    <path d="M14.5 3.5h3" />
    <path d="M18.5 3.5l-1.5 1.5" />
    <path d="M13.5 3.5l1.5 1.5" />
  </svg>
);

export type AccountToolbarRefreshButtonProps = {
  refreshing: boolean;
  onRefresh: () => void;
  lastUpdatedAt?: string | Date | null;
};

export const AccountToolbarRefreshButton = ({
  refreshing,
  onRefresh,
  lastUpdatedAt,
}: AccountToolbarRefreshButtonProps) => (
  <RefreshButtonWithMeta
    refreshing={refreshing}
    onRefresh={onRefresh}
    lastUpdatedAt={lastUpdatedAt}
  />
);

const RetailAccountCardToolbar = ({
  account,
  refreshing,
  rebalancing,
  updating = false,
  togglingSignalForge = false,
  onDeactivate,
  onRefresh,
  onToggleSignalForge,
  onRebalance,
  lastUpdatedAt,
  showDeactivate = true,
  showRefresh = true,
  showSignalForge = true,
  showRebalance = true,
}: RetailAccountCardToolbarProps) => (
  <>
    {showDeactivate && onDeactivate && (
      <button
        type="button"
        className={`${TOOLBAR_BTN_BASE} ${
          account.active ? TOOLBAR_TONE_WARN : TOOLBAR_TONE_ACTIVATE
        }`}
        onClick={onDeactivate}
        disabled={updating}
        title={account.active ? 'Deactivate this account' : 'Activate this account'}
      >
        <DeactivateIcon />
        <span>
          {updating ? 'Updating…' : account.active ? 'Deactivate' : 'Activate'}
        </span>
      </button>
    )}
    {showRefresh && (
      <AccountToolbarRefreshButton
        refreshing={refreshing}
        onRefresh={onRefresh}
        lastUpdatedAt={lastUpdatedAt}
      />
    )}
    {showSignalForge && onToggleSignalForge && (
      <button
        type="button"
        className={`${TOOLBAR_BTN_BASE} min-w-0 ${
          account.signal_forge_enabled ? TOOLBAR_TONE_SF_ON : TOOLBAR_TONE_SF_OFF
        }`}
        onClick={onToggleSignalForge}
        disabled={togglingSignalForge || !account.active}
        title={
          !account.active
            ? 'Activate account to use SignalForge'
            : account.signal_forge_enabled
              ? 'Disable SignalForge for this account'
              : 'Enable SignalForge for this account'
        }
      >
        <SignalForgeIcon on={account.signal_forge_enabled} />
        <span>
          {togglingSignalForge
            ? 'Updating…'
            : account.signal_forge_enabled
              ? 'SignalForge on'
              : 'SignalForge off'}
        </span>
      </button>
    )}
    {showRebalance && (
      <button
        type="button"
        className={`${TOOLBAR_BTN_BASE} ${TOOLBAR_TONE_REBALANCE}`}
        onClick={onRebalance}
        disabled={rebalancing || !account.active}
        title="Force rebalance this account"
      >
        <RebalanceIcon spinning={rebalancing} />
        <span>{rebalancing ? 'Rebalancing…' : 'Force Rebalance'}</span>
      </button>
    )}
  </>
);

export default RetailAccountCardToolbar;
